/**
 * Concurrent-request de-dupe (settings INP structural follow-up, 2026-07-16).
 *
 * Several settings sections mount in the SAME React commit and each fetch the
 * same endpoint on mount: the athlete `/v2/settings` page mounts
 * SubscriptionSection AND GuardiansSection, and BOTH call `useUserTier`
 * (GET /api/user/tier) and `useRelationships` (GET /api/relationships) — so
 * each endpoint was hit twice per page load.
 *
 * `sharedRequest` collapses those concurrent duplicates: the first caller for a
 * key runs `fn` and registers its in-flight promise synchronously; any caller
 * that asks for the same key while that promise is still pending receives the
 * SAME promise instead of firing a second request. The entry is removed as soon
 * as the promise settles (success OR failure), so this is strictly a
 * same-flight share — NOT a cache. A later call (a remount, or an explicit
 * refetch after a mutation) always starts a fresh request and observes fresh
 * server state.
 *
 * Chosen over lifting the hooks into a shared context provider because the
 * consumers keep their existing independent state (GuardiansSection mutates +
 * refetches on its own; SubscriptionSection is read-only) — a provider would
 * force a lift + rewire across every consumer for no extra dedupe benefit.
 * This is the least-invasive fix that keeps the hooks' public API byte-identical.
 *
 * Note: because the share window is only the in-flight interval, the two
 * consumers must mount in the same flight for the dedupe to collapse them. On
 * the settings page they do — SubscriptionSection + GuardiansSection mount in
 * one commit (and jsdom, where the pin runs, mounts every section at once).
 */

const inFlight = new Map<string, Promise<unknown>>();

/**
 * Share an in-flight async request across concurrent callers keyed by `key`.
 * The `fn` producer is invoked at most once per in-flight window.
 */
export function sharedRequest<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = fn();
  inFlight.set(key, promise);
  // Clear on settle so the entry never outlives its request (no stale cache);
  // swallow here so this bookkeeping never becomes an unhandled rejection — the
  // real callers still await `promise` and handle success/failure themselves.
  void Promise.resolve(promise)
    .catch(() => {})
    .finally(() => {
      if (inFlight.get(key) === promise) inFlight.delete(key);
    });
  return promise;
}

/** Test-only: drop any in-flight entries so counts don't leak across tests. */
export function _resetSharedRequests(): void {
  inFlight.clear();
}

/**
 * B242 (Opus sign-off finding 3, 2026-09-23) — drop the in-flight entry for
 * `key`, but only when it is still `promise` (the B47 fix 10 pattern,
 * `useAccountRole.ts`). A caller that stops waiting before the request
 * settles (a deadline) must not delete a DIFFERENT, later request that has
 * already replaced this one for the same key. The entry that settles
 * normally already clears itself above; this is for the caller that gives up
 * BEFORE that happens, so the next mount for the same key issues a fresh
 * request instead of rejoining the one this caller already abandoned.
 */
export function dropSharedRequest(key: string, promise: Promise<unknown>): void {
  if (inFlight.get(key) === promise) inFlight.delete(key);
}
